
import { User } from '../types';
import { auth, googleProvider, isFirebaseEnabled } from './firebase';
import { signInWithPopup, signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signOut, sendPasswordResetEmail } from 'firebase/auth';

// --------------------------------------------------------------------------
// LOCAL STORAGE FALLBACK (Used when Firebase is not configured or offline)
// --------------------------------------------------------------------------
const STORAGE_KEY_USERS = 'paycircle_local_users';
const STORAGE_KEY_SESSION = 'paycircle_session_user';
const SESSION_TIMESTAMP_KEY = 'paycircle_session_expiry';

const getLocalUsers = (): User[] => {
  const stored = localStorage.getItem(STORAGE_KEY_USERS);
  if (stored) return JSON.parse(stored);
  return [];
};

const saveLocalUser = (user: User) => {
  const users = getLocalUsers();
  if (!users.find(u => u.id === user.id)) {
    users.push(user);
    localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(users));
  }
};

// --------------------------------------------------------------------------
// AUTH SERVICE
// --------------------------------------------------------------------------

export const authService = {

  // Reset Password via Firebase Auth or Fallback
  resetPassword: async (email: string): Promise<void> => {
    const cleanEmail = email ? email.trim() : '';
    if (!cleanEmail) {
      throw new Error("Please enter a valid email address.");
    }
    if (isFirebaseEnabled && auth) {
      try {
        const actionCodeSettings = {
          url: typeof window !== 'undefined' ? `${window.location.origin}/login` : 'https://fairshare-9ce5d.web.app/login',
          handleCodeInApp: true
        };
        await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
      } catch (error: any) {
        console.error('[Auth] sendPasswordResetEmail error:', error);
        if (error.code === 'auth/user-not-found') {
          throw new Error("No account found with this email address. If you registered using Google, please sign in with Google.");
        } else if (error.code === 'auth/invalid-email') {
          throw new Error("Invalid email address format.");
        } else if (error.code === 'auth/too-many-requests') {
          throw new Error("Too many requests. Please wait a few minutes before trying again.");
        } else {
          throw new Error(error.message || "Failed to send password reset email. Please try again.");
        }
      }
    } else {
      // Local fallback mode simulation
      await new Promise(resolve => setTimeout(resolve, 800));
      console.log(`[Auth Mock] Password reset email simulated for: ${cleanEmail}`);
    }
  },

  // Direct login as Offline Guest
  loginAsOfflineGuest: async (customName?: string): Promise<User> => {
    const guestLang = localStorage.getItem('paycircle_guest_lang');
    const guestName = customName || 'Guest User';
    const guestUser: User = {
      id: 'guest_' + Date.now(),
      name: guestName,
      email: 'guest@paycircle.local',
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(guestName)}&background=6366f1&color=fff`,
      language: guestLang || 'en'
    };
    saveLocalUser(guestUser);
    localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(guestUser));
    authService.updateSessionActivity();
    return guestUser;
  },

  // Login with Google (with Offline/Network Fallback)
  loginWithGoogle: async (): Promise<User> => {
    const guestLang = localStorage.getItem('paycircle_guest_lang');
    if (isFirebaseEnabled && auth && googleProvider) {
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const fbUser = result.user;
        const user: User = {
          id: fbUser.uid,
          name: fbUser.displayName || 'Google User',
          email: fbUser.email || '',
          avatar: fbUser.photoURL || `https://ui-avatars.com/api/?name=User`,
          language: guestLang || 'en'
        };
        localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(user));
        authService.updateSessionActivity();
        return user;
      } catch (error: any) {
        // If network request failed, referer is blocked, or user is offline, fallback to offline guest mode seamlessly
        if (error.code === 'auth/network-request-failed' || error.message?.includes('requests-from-referer') || error.message?.includes('blocked') || !navigator.onLine) {
          console.warn('[Auth] Network unreachable or API key domain restricted during Google sign-in. Falling back to offline guest session.');
          return authService.loginAsOfflineGuest('Offline Guest');
        }
        throw new Error(error.message);
      }
    } else {
      // Fallback Simulation
      await new Promise(resolve => setTimeout(resolve, 1500));
      const user: User = {
        id: 'google_sim_' + Date.now(),
        name: 'Simulated Google User',
        email: 'google_sim@gmail.com',
        avatar: 'https://lh3.googleusercontent.com/a/default-user=s100',
        language: guestLang || 'en'
      };
      saveLocalUser(user);
      localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(user));
      authService.updateSessionActivity();
      return user;
    }
  },

  // Login with Email (with Offline / Network Fallback)
  loginWithEmail: async (email: string, pass: string): Promise<User> => {
    const guestLang = localStorage.getItem('paycircle_guest_lang');
    if (isFirebaseEnabled && auth) {
      try {
        const result = await signInWithEmailAndPassword(auth, email, pass);
        const fbUser = result.user;
        const user: User = {
          id: fbUser.uid,
          name: fbUser.displayName || email.split('@')[0],
          email: fbUser.email || email,
          avatar: fbUser.photoURL || `https://ui-avatars.com/api/?name=${email}`,
          language: guestLang || 'en'
        };
        // PERSIST SESSION
        localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(user));
        authService.updateSessionActivity();
        return user;
      } catch (error: any) {
        if (error.code === 'auth/network-request-failed' || error.message?.includes('requests-from-referer') || error.message?.includes('blocked') || !navigator.onLine) {
          // Check local cached users first if network fails or API key is domain-restricted
          const users = getLocalUsers();
          const localUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
          if (localUser) {
            localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(localUser));
            authService.updateSessionActivity();
            return localUser;
          }
          console.warn('[Auth] Network error or API key domain restriction during Email login. Returning offline guest session.');
          return authService.loginAsOfflineGuest(email.split('@')[0] + ' (Offline)');
        }
        throw new Error(error.message);
      }
    } else {
      // Fallback
      await new Promise(resolve => setTimeout(resolve, 800));
      const users = getLocalUsers();
      const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());

      if (user) {
        if (guestLang) user.language = guestLang;
        localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(user));
        authService.updateSessionActivity();
        return user;
      }

      throw new Error("User not found or mock password incorrect.");
    }
  },

  // Register
  register: async (email: string, pass: string, name: string): Promise<User> => {
    const guestLang = localStorage.getItem('paycircle_guest_lang');
    if (isFirebaseEnabled && auth) {
      try {
        const result = await createUserWithEmailAndPassword(auth, email, pass);
        await updateProfile(result.user, { displayName: name });
        const user: User = {
          id: result.user.uid,
          name: name,
          email: result.user.email || email,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}`,
          language: guestLang || 'en'
        };
        // PERSIST SESSION
        localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(user));
        authService.updateSessionActivity();
        return user;
      } catch (error: any) {
        if (error.message?.includes('requests-from-referer') || error.message?.includes('blocked') || error.code === 'auth/network-request-failed') {
          console.warn('[Auth] Firebase API Key domain restricted. Creating local account session.');
          const newUser: User = {
            id: 'loc_' + Date.now(),
            name,
            email,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=10b981&color=fff`,
            language: guestLang || 'en'
          };
          saveLocalUser(newUser);
          localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(newUser));
          authService.updateSessionActivity();
          return newUser;
        }
        throw new Error(error.message);
      }
    } else {
      // Fallback
      await new Promise(resolve => setTimeout(resolve, 1000));
      const users = getLocalUsers();
      if (users.find(u => u.email === email)) throw new Error("Email already in use (Local DB).");

      const newUser: User = {
        id: 'loc_' + Date.now(),
        name,
        email,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=10b981&color=fff`,
        language: guestLang || 'en'
      };
      saveLocalUser(newUser);
      localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(newUser));
      authService.updateSessionActivity();
      return newUser;
    }
  },

  logout: async () => {
    if (isFirebaseEnabled && auth) {
      try {
        await signOut(auth);
      } catch (e) {
        console.warn('[Auth] Firebase signOut encountered an error (e.g. offline):', e);
      }
    }
    localStorage.removeItem(STORAGE_KEY_SESSION);
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);
    localStorage.removeItem('paycircle_session_expired');
    localStorage.removeItem('paycircle_session_timestamp'); // legacy cleanup
  },

  // Get current session from local (for initial load speed or fallback)
  getCurrentUser: (): User | null => {
    const stored = localStorage.getItem(STORAGE_KEY_SESSION);
    return stored ? JSON.parse(stored) : null;
  },

  getAllUsers: (): User[] => {
    return getLocalUsers();
  },

  updateSessionActivity: () => {
    localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
    try {
      localStorage.removeItem('paycircle_session_expired');
    } catch (e) {
      console.error(e);
    }
  },

  // Persistent session management: user sessions stay active indefinitely until explicit logout
  isSessionExpired: (): boolean => {
    return false;
  }
};
